/**
 * HTTP-клиент Front — экземпляр `createApiClient` (`@srm/core/http`). Путь `@/http` — модуль-экземпляр: default export
 * (`$api`) импортируют сервисы, его мокают тесты. Bearer, ротация на 401, обслуживание, общий тост и разбор отказа —
 * клиент ядра; своё у Front — плагины. Их порядок: сначала запреты вкладок поддержки (предпросмотр, вход «от имени»),
 * затем заголовки (версия клиента, показ экрана), затем наблюдатели (журнал поддержки, скрытые действия).
 */
import { createApiClient, MAINTENANCE_PATH } from '@srm/core/http';
import { clientVersionPlugin } from '@srm/core/version';
import { sendNotification } from '@srm/ui/lib/notifications';
import { auth } from '@/auth';
import { APP_VERSION } from '@/configuration/appVersion';
import { ConfigService } from '@/configuration/services/configService';
import { versionUpdate } from '@utils/versionUpdate';
import { actionLoadErrorsPlugin } from './actionLoadErrors';
import { serializeParams } from './paramsSerializer';
import { supportJournalPlugin } from './supportJournal';
import { supportPreviewGuardPlugin } from './supportPreviewRequestGuard';
import { supportSessionGuardPlugin } from './supportSessionGuard';
import { supportShareHeaderPlugin } from './supportShareHeader';

const config = ConfigService.get();

let canSeeFailureToast: () => boolean = () => false;

/** Стор глазами клиента: только право на админку (тип стора здесь замкнул бы импорт в цикл). */
interface IToastAudienceStore {
    getState(): { auth: { user: { canView?: { adminPanel?: boolean } } } };
}

/**
 * Кто видит общий тост отказа на PROD (EUPDEV-11813): там он пугает рядовых пользователей и плодит обращения в ТП —
 * показываем администраторам. index.tsx передаёт стор; вне PROD тост видят все.
 */
export const setGenericToastAudience = (store: IToastAudienceStore): void => {
    canSeeFailureToast = () => !!store.getState().auth.user.canView?.adminPanel;
};

const client = createApiClient({
    baseURL: config.application.apiUrl,
    withCredentials: config.application.withCredentials,
    auth,
    notify: (notification) => sendNotification(notification),
    shouldNotifyFailure: () => config.environment !== 'PROD' || canSeeFailureToast(),
    paramsSerializer: serializeParams,
    // Страница обслуживания — своя: на ней переход не нужен.
    maintenance: { selfPath: MAINTENANCE_PATH },
    plugins: [
        supportPreviewGuardPlugin(),
        supportSessionGuardPlugin(auth),
        clientVersionPlugin({ version: APP_VERSION, onUpdateRequired: () => versionUpdate.require() }),
        supportShareHeaderPlugin(),
        supportJournalPlugin(),
        actionLoadErrorsPlugin(),
    ],
});

export const API_URL: string = client.baseURL;

/**
 * Запрос при уходе со страницы (pagehide) мимо axios — те же плагины (версия клиента, показ экрана поддержке, запреты
 * вкладок поддержки) и Bearer. `path` — от `API_URL`; false — не отправлен (тело больше лимита keepalive или запрос
 * из предпросмотра поддержки).
 */
export const sendKeepalive = client.sendKeepalive;

export default client.http;
